import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { validate } from '../../../validation/engine';
import ResponseShell from './ResponseShell';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { useAutosave } from './useAutosave';
import { computeResponseReadOnly } from './responseShellTypes';
import type { ProcedureResponseProps } from './responseShellTypes';
import {
  responseService,
  transitionService,
  formTemplateService,
  findingService,
  procedureEvidenceService,
  type FormTemplateItem,
  type Finding,
  type ProcedureEvidence,
} from '../../../services/auditService';
import { useAuth } from '../../../contexts/AuthContext';
import ObservationModal, { type ObservationModalRowContext } from './ObservationModal';
import RaiseFindingButton, { ITEM_TYPE_FORM_RESPONSE } from './RaiseFindingButton';
import InlineQuestionMeta from './InlineQuestionMeta';
import EvidenceCell from './EvidenceCell';
import RejectNoteCell from './RejectNoteCell';

/**
 * FormResponse — body for procedure_type='Form'.
 *
 * G4 (phase 108) — ENTITY-WIDE rework. Per the 2026-06-19 decision (NO DB
 * migration), the Form procedure is answered ONCE per procedure_instance, NOT
 * once per sample. `ADT_form_responses` only has (form_template_id,
 * form_question, answer_value) — there is no sample_item_id / field_id column —
 * so the previous sampleItems × fields cross-product produced rows the backend
 * dropped (Pydantic extra='ignore'), a 422 on create (required form_question
 * was never sent), and a reload that filtered on field_id != null so nothing
 * mapped back.
 *
 * The fix mirrors the shipped sample-less QuestionnaireResponse:
 *   - Render the template fields ONCE (no sample loop, no per-sample switcher).
 *   - Save each dirty field as { entity_id, procedure_instance_id,
 *     form_template_id, form_question, answer_value }.
 *   - Reload by matching `form_question` back to the template field.
 *   - Auth is execution-access (108-04 backend swap) so makers without an
 *     ACT_FINDINGS grant can save.
 *
 * Each template field uses FormControl widgets typed by
 * ADT_form_template_items.answer_data_type (Text/Date/Number/Decimal/
 * Percentage/Email/PAN/Dropdown). The 108-01 readOnly lock and the 108-03
 * RejectNoteCell per-line control are preserved per field.
 */

type FieldType = 'Text' | 'Date' | 'Number' | 'Decimal' | 'Percentage' | 'Email' | 'PAN' | 'Dropdown';

interface FormRow {
  /** ADT_form_template_items.id — stable React key only (not persisted). */
  template_field_id: number;
  /** Inline question text — the PERSISTED identity (matches form_question). */
  form_question: string;
  field_type: FieldType;
  options: string[];
  is_required: boolean;
  answer_value: string;
  /** Per-field remark/observation text. */
  remark: string;
  /** Existing ADT_form_responses.id once saved/loaded from server. */
  response_id?: number;
  /** G2 / 108-03 — checker per-line reject note (non-empty => line rejected). */
  reject_note: string;
  dirty: boolean;
}

const SUPPORTED_FIELD_TYPES: ReadonlyArray<FieldType> = [
  'Text', 'Date', 'Number', 'Decimal', 'Percentage', 'Email', 'PAN', 'Dropdown',
];

function normaliseFieldType(raw: string | undefined | null): FieldType {
  const v = (raw ?? 'Text').trim();
  if ((SUPPORTED_FIELD_TYPES as ReadonlyArray<string>).includes(v)) return v as FieldType;
  return 'Text';
}

function parseOptions(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw.split(/[,;\n]/).map(s => s.trim()).filter(s => s.length > 0);
}

// Template-typed validation code per ADT_form_template_items.answer_data_type.
// Text → 110 (free-text description; the form answer is a free response, not a
// short identity), Email → 401, Number/Decimal/Percentage → 206, Date → 305.
// PAN already enforces its own pattern attribute on the input and has no
// dedicated catalog code, so it is left unwired; Dropdown is a select (leave).
function validationCodeFor(t: FieldType): number | null {
  switch (t) {
    case 'Text': return 110;
    case 'Email': return 401;
    case 'Number':
    case 'Decimal':
    case 'Percentage': return 206;
    case 'Date': return 305;
    case 'PAN':
    case 'Dropdown':
    default: return null;
  }
}

function renderField(
  row: FormRow,
  onChange: (v: string) => void,
  readOnly: boolean,
  onBlur: (v: string) => void,
): React.ReactNode {
  const tid = `form_response_${row.template_field_id}`;
  switch (row.field_type) {
    case 'Text':
      return (
        <FormControl.Input
          type="text"
          data-testid={`INP_${tid}`}
          value={row.answer_value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => onBlur(e.target.value)}
          disabled={readOnly}
          noMargin
        />
      );
    case 'Email':
      return (
        <FormControl.Input
          type="email"
          data-testid={`INP_${tid}`}
          value={row.answer_value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => onBlur(e.target.value)}
          disabled={readOnly}
          noMargin
        />
      );
    case 'PAN':
      return (
        <FormControl.Input
          type="text"
          data-testid={`INP_${tid}`}
          value={row.answer_value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value.toUpperCase())}
          pattern="^[A-Z]{5}[0-9]{4}[A-Z]$"
          maxLength={10}
          placeholder="ABCDE1234F"
          disabled={readOnly}
          noMargin
        />
      );
    case 'Number':
      return (
        <FormControl.Input
          type="number"
          step="1"
          data-testid={`INP_${tid}`}
          value={row.answer_value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => onBlur(e.target.value)}
          disabled={readOnly}
          noMargin
        />
      );
    case 'Decimal':
      return (
        <FormControl.Input
          type="number"
          step="0.01"
          data-testid={`INP_${tid}`}
          value={row.answer_value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => onBlur(e.target.value)}
          disabled={readOnly}
          noMargin
        />
      );
    case 'Percentage':
      return (
        <FormControl.Input
          type="number"
          step="0.01"
          min={0}
          max={100}
          data-testid={`INP_${tid}`}
          value={row.answer_value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => onBlur(e.target.value)}
          addon="%"
          disabled={readOnly}
          noMargin
        />
      );
    case 'Date':
      return (
        <FormControl.DatePicker
          data-testid={`INP_${tid}`}
          value={row.answer_value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => onBlur(row.answer_value)}
          disabled={readOnly}
          noMargin
        />
      );
    case 'Dropdown':
      return (
        <FormControl.SearchSelect
          data-testid={`SEL_${tid}`}
          value={row.answer_value}
          onChange={(v) => onChange(v == null ? '' : String(v))}
          options={row.options.map(o => ({ value: o, label: o }))}
          placeholder="Select…"
          clearable
          disabled={readOnly}
          noMargin
        />
      );
  }
}

const FormResponse: React.FC<ProcedureResponseProps> = ({
  instance,
  role,
  performerName,
  approverName,
  onSaved,
  readOnly: readOnlyProp,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const currentUserId = state.user?.id ?? 0;
  // G3 (108-01) — an Approved instance is immutable. Derive from workflow_state
  // Read-only unless the maker (performer) is editing an open instance — shared rule.
  const readOnly = computeResponseReadOnly(instance, role, readOnlyProp);

  const [rows, setRows] = useState<FormRow[]>([]);
  const [template, setTemplate] = useState<FormTemplateItem[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [obsErrors, setObsErrors] = useState<Record<string, string>>({});

  // Inline finding raise state — for the entity-wide Form, findings attach at
  // the form_response level (item_type_id=FORM_RESPONSE), mirroring the
  // questionnaire fallback. No sample ids.
  const [findings, setFindings] = useState<Finding[]>([]);
  const [modalRow, setModalRow] = useState<{
    context: ObservationModalRowContext;
    itemRecordId: number | null;
  } | null>(null);
  // Phase 113 REQ-8 — per-question evidence, fetched once and grouped.
  const [evidenceByQuestion, setEvidenceByQuestion] = useState<Map<string, ProcedureEvidence[]>>(new Map());

  // Per-field remarks modal — holds the index of the row being edited.
  const [remarksRow, setRemarksRow] = useState<number | null>(null);

  const reloadFindings = useCallback(async () => {
    try {
      const list = await findingService.listForProcedure(instance.id);
      setFindings(list);
    } catch {
      // Non-fatal — badges just won't show. Body still works.
    }
  }, [instance.id]);

  const reloadEvidence = useCallback(async () => {
    try {
      const list = await procedureEvidenceService.list(instance.id);
      const map = new Map<string, ProcedureEvidence[]>();
      for (const ev of list) {
        if (ev.item_type_id != null && ev.item_record_id != null) {
          const key = `${ev.item_type_id}-${ev.item_record_id}`;
          const bucket = map.get(key) ?? [];
          bucket.push(ev);
          map.set(key, bucket);
        }
      }
      setEvidenceByQuestion(map);
    } catch {
      // Non-fatal — inline list renders empty.
    }
  }, [instance.id]);

  useEffect(() => {
    reloadFindings();
    reloadEvidence();
  }, [reloadFindings, reloadEvidence]);

  const findingsForRow = useCallback(
    (itemRecordId: number | null): Finding[] => {
      if (itemRecordId == null) return [];
      return findings.filter(
        (f) =>
          f.item_type_id === ITEM_TYPE_FORM_RESPONSE && f.item_record_id === itemRecordId,
      );
    },
    [findings],
  );

  // Load template fields + existing responses; map responses to fields by
  // form_question (entity-wide identity — NO sample_item_id / field_id filter).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const templateId = instance.procedure_template_id ?? null;
        let fields: FormTemplateItem[] = [];
        if (templateId != null) {
          fields = await formTemplateService.listForProcedure(templateId);
        }
        const responses = await responseService.list('Form', instance.id);
        // Entity-wide Form has no per-field id in the DB, so the only key back to a
        // field is form_question. Two template fields can legitimately share the same
        // question text — bucket responses per question and consume them positionally
        // so identical-text fields map to DISTINCT response rows (no collision).
        const byQuestion = new Map<string, any[]>();
        for (const r of responses) {
          if (r.form_question == null) continue;
          const k = String(r.form_question);
          const bucket = byQuestion.get(k);
          if (bucket) bucket.push(r);
          else byQuestion.set(k, [r]);
        }
        const built: FormRow[] = fields.map((tf) => {
          const bucket = byQuestion.get(tf.form_question);
          const existing = bucket && bucket.length ? bucket.shift() : undefined;
          return {
            template_field_id: tf.id,
            form_question: tf.form_question,
            field_type: normaliseFieldType(tf.answer_data_type),
            options: parseOptions(tf.options ?? null),
            is_required: !!tf.is_required,
            answer_value: existing?.answer_value ?? '',
            remark: existing?.remark ?? '',
            response_id: existing?.id,
            reject_note: existing?.reject_note ?? '',
            dirty: false,
          };
        });
        if (!cancelled) {
          setTemplate(fields);
          setRows(built);
        }
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load form template.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance.id, instance.procedure_template_id]);

  // Latest persisted response_id per template_field_id — readable synchronously
  // right after flush() (state updates are async, so a ref is the reliable
  // source for the Raise-Finding handler that needs the id immediately).
  // Keyed by the stable, unique field id (NOT form_question, which can repeat).
  const idsRef = useRef<Record<number, number>>({});

  const save = useCallback(async () => {
    // UVST: skip rows with a per-field validation error (obsErrors keyed by
    // template_field_id) so invalid input is never autosaved.
    const dirty = rows.filter((r) => r.dirty && !obsErrors[r.template_field_id]);
    if (dirty.length === 0) return;

    const updates: Array<{ field_id: number; response_id?: number }> = [];
    for (const r of dirty) {
      if (r.response_id) {
        await responseService.update('Form', r.response_id, {
          form_question: r.form_question,
          answer_value: r.answer_value,
          modified_by: currentUserId,
        });
        updates.push({ field_id: r.template_field_id, response_id: r.response_id });
        idsRef.current[r.template_field_id] = r.response_id;
      } else {
        const created = await responseService.create('Form', {
          entity_id: entityId,
          procedure_instance_id: instance.id,
          // form_template_id FKs adt_form_templates(id) — that's the form FIELD row
          // (template_field_id), NOT the procedure template id. Sending the procedure
          // template id caused a ForeignKeyViolation and Form answers never saved.
          form_template_id: r.template_field_id,
          form_question: r.form_question,
          answer_value: r.answer_value,
          created_by: currentUserId,
        });
        updates.push({ field_id: r.template_field_id, response_id: created?.id });
        if (created?.id) idsRef.current[r.template_field_id] = created.id;
      }
    }

    setRows((prev) =>
      prev.map((r) => {
        const upd = updates.find((u) => u.field_id === r.template_field_id);
        if (!upd) return r;
        return { ...r, response_id: upd.response_id ?? r.response_id, dirty: false };
      }),
    );
  }, [rows, obsErrors, entityId, currentUserId, instance.id, instance.procedure_template_id]);

  const { status, markDirty, flush } = useAutosave(save, 1000);

  const updateRow = (idx: number, v: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], answer_value: v, dirty: true };
      return next;
    });
    // UVST: clear any inline error as the performer edits (cleared on change,
    // re-validated on blur — ChecklistResponse idiom), keyed by template_field_id.
    const row = rows[idx];
    if (row) {
      setObsErrors(prev => { const n = { ...prev }; delete n[row.template_field_id]; return n; });
    }
    markDirty();
  };

  // UVST: per-field validation on blur, typed by the template field's
  // answer_data_type (validationCodeFor). ChecklistResponse obsErrors idiom,
  // keyed by template_field_id; the autosave filter skips rows with an error.
  const blurCell = (row: FormRow, value: string) => {
    const k = row.template_field_id;
    const code = validationCodeFor(row.field_type);
    if (code == null || !value || !value.trim()) {
      setObsErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
      return;
    }
    const r = validate(code, value);
    if (!r.valid) setObsErrors(prev => ({ ...prev, [k]: r.message ?? 'Invalid value.' }));
    else setObsErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
  };

  // Raise/attach a finding for a field row. Persist any unsaved answer first so
  // the finding links to the form_response row; falls back to a procedure-level
  // finding (null record id) when the row has no answer yet. Mirrors
  // QuestionnaireResponse.openFinding — no sample ids.
  const openFinding = useCallback(
    async (r: FormRow) => {
      let recordId: number | null = r.response_id ?? idsRef.current[r.template_field_id] ?? null;
      if (recordId == null && (r.answer_value ?? '').trim()) {
        await flush();
        recordId = idsRef.current[r.template_field_id] ?? null;
      }
      setModalRow({
        context: {
          procedureName: instance.audit_procedure_name,
          sampleLabel: null,
          responseValue: r.answer_value || null,
        },
        itemRecordId: recordId,
      });
    },
    [flush, instance.audit_procedure_name],
  );

  if (loadError) {
    return <div className="response-load-error">{loadError}</div>;
  }
  if (template.length === 0) {
    return (
      <div className="response-shell-body modal-empty-text">
        {instance.procedure_template_id == null
          ? 'Awaiting template — no procedure_template_id on this instance.'
          : 'Loading template…'}
      </div>
    );
  }

  // G2 / 108-03 — number of lines a checker has rejected (drives the approve-gate).
  const rejectedCount = rows.filter(r => r.reject_note && r.reject_note.trim()).length;

  // Submit gate (2026-06-29) — EVERY question must be answered before the maker
  // can submit. A field is "answered" when its answer_value is a non-empty string
  // (covers Text/Date/Number/Dropdown alike; an unpicked dropdown stays '').
  const unanswered = rows.filter(r => !(r.answer_value ?? '').trim()).length;
  const incompleteReason =
    rows.length > 0 && unanswered > 0
      ? `Answer all ${rows.length} questions before submitting — ${unanswered} still unanswered.`
      : null;

  return (
    <ResponseShell
      instance={instance}
      procedureType="Form"
      role={role}
      performerName={performerName}
      approverName={approverName}
      autosaveStatus={status}
      rejectedCount={rejectedCount}
      incompleteReason={incompleteReason}
      onSaveDraft={async () => {
        await flush();
      }}
      onSubmit={async () => {
        await flush();
        await transitionService.submit('procedure', instance.id);
        onSaved?.();
      }}
      onApprove={async () => {
        await transitionService.approve('procedure', instance.id);
        onSaved?.();
      }}
      onReject={async (comment) => {
        await transitionService.reject('procedure', instance.id, comment);
        onSaved?.();
      }}
      onReopen={async () => {
        await transitionService.reopen('procedure', instance.id);
        onSaved?.();
      }}
    >
      {loadError && <div className="response-load-error">{loadError}</div>}
      {!loadError && template.length === 0 && (
        <div className="response-shell-body modal-empty-text">
          {instance.procedure_template_id == null
            ? 'Awaiting template — no procedure_template_id on this instance.'
            : 'Loading template…'}
        </div>
      )}

      {template.length > 0 && (
        <div className="response-section">
          <div className="table-list response-qtable">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <colgroup>
                  <col className="response-col-sno" />
                  <col />
                  <col className="response-col-answer-wide" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="table-list-th response-th-sno">S.No</th>
                    <th className="table-list-th">Question</th>
                    <th className="table-list-th">Answer</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const rowFindings = findingsForRow(r.response_id ?? null);
                    const evKey = `${ITEM_TYPE_FORM_RESPONSE}-${r.response_id ?? 0}`;
                    const rowEvidence = r.response_id ? (evidenceByQuestion.get(evKey) ?? []) : [];
                    return (
                      <React.Fragment key={`fld-${r.template_field_id}`}>
                        <tr className="table-list-row">
                          <td className="table-list-cell response-cell-sno">{idx + 1}</td>
                          <td className="table-list-cell response-cell-question">
                            {/* REQ-7: question text LEFT, action cluster RIGHT, same flex row. */}
                            <div className="response-question-row">
                              <span className="response-question-text">
                                {r.form_question}
                                {r.is_required && (
                                  <span className="fg__required" aria-hidden="true">*</span>
                                )}
                              </span>
                              <div className="response-action-cluster response-action-cluster--inline">
                                <button
                                  type="button"
                                  className="response-row-action"
                                  aria-label="Add remark"
                                  title={r.remark ? 'Edit remark' : 'Add remark'}
                                  disabled={readOnly}
                                  onClick={() => setRemarksRow(idx)}
                                >
                                  <MessageSquare size={14} />
                                </button>
                                <EvidenceCell
                                  scope="row"
                                  evidenceType={undefined}
                                  entityId={entityId}
                                  createdBy={currentUserId}
                                  procedureInstanceId={instance.id}
                                  itemTypeId={ITEM_TYPE_FORM_RESPONSE}
                                  itemRecordId={r.response_id ?? null}
                                  questionLabel={r.form_question}
                                  disabled={readOnly}
                                  showCount={false}
                                  onChanged={reloadEvidence}
                                />
                                <RejectNoteCell
                                  responseId={r.response_id}
                                  rejectNote={r.reject_note}
                                  procedureType="Form"
                                  role={role}
                                  readOnly={readOnly}
                                  onSaved={(note) =>
                                    setRows(prev => prev.map((x) =>
                                      x.template_field_id === r.template_field_id
                                        ? { ...x, reject_note: note }
                                        : x,
                                    ))
                                  }
                                />
                                <RaiseFindingButton
                                  count={rowFindings.length}
                                  showCount={false}
                                  disabled={readOnly}
                                  aria-label="Raise finding"
                                  onClick={() => { void openFinding(r); }}
                                  title="Raise a finding from this row"
                                />
                              </div>
                            </div>
                            {/* REQ-8 rev2: stacked Remarks / Attachments / Findings areas. */}
                            <InlineQuestionMeta
                              remark={r.remark}
                              evidence={rowEvidence}
                              findings={rowFindings}
                              readOnly={readOnly}
                              onEvidenceRemoved={reloadEvidence}
                              onRemarkClick={readOnly ? undefined : () => setRemarksRow(idx)}
                            />
                          </td>
                          <td className="table-list-cell">
                            {renderField(r, (v) => updateRow(idx, v), readOnly, (v) => blurCell(r, v))}
                            {obsErrors[r.template_field_id] && (
                              <div className="response-inline-warning">
                                {obsErrors[r.template_field_id]}
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                  {rows.length === 0 && !loadError && (
                    <tr>
                      <td className="table-list-cell modal-empty-text" colSpan={3}>
                        Loading…
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Single ObservationModal + FindingDetailModal */}
      <ObservationModal
        isOpen={modalRow !== null}
        onClose={() => setModalRow(null)}
        procedureInstanceId={instance.id}
        entityId={entityId}
        currentUserId={currentUserId}
        rowContext={modalRow?.context ?? { procedureName: instance.audit_procedure_name }}
        itemTypeId={ITEM_TYPE_FORM_RESPONSE}
        itemRecordId={modalRow?.itemRecordId ?? null}
        onCreated={(f) => {
          setFindings((prev) => [...prev, f]);
          reloadFindings();
        }}
      />
      {/* Per-field remarks modal */}
      {remarksRow !== null && rows[remarksRow] && (() => {
        const ridx = remarksRow;
        const row = rows[ridx];
        return (
          <Modal isOpen onClose={() => setRemarksRow(null)} size="md">
            <Modal.Header onClose={() => setRemarksRow(null)}>{`Remarks under "${row.form_question}"`}</Modal.Header>
            <Modal.Body>
              <FormControl.Textarea
                label="Remark"
                data-testid={`INP_form_response_remark_${row.template_field_id}`}
                value={row.remark}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setRows((prev) =>
                    prev.map((x) =>
                      x.template_field_id === row.template_field_id
                        ? { ...x, remark: e.target.value }
                        : x,
                    ),
                  );
                }}
                rows={4}
                autoFocus
                disabled={readOnly}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="fill" onClick={() => setRemarksRow(null)}>Done</Button>
            </Modal.Footer>
          </Modal>
        );
      })()}
    </ResponseShell>
  );
};

export default FormResponse;
